import * as React from "react";
import {Checkbox, Col, FormControl, HelpBlock} from "react-bootstrap";
import {defineMessages, InjectedIntl} from "react-intl";
import {DateUtil} from "../../util/DateUtil";
import {TextEdit} from "../edit/TextEdit";
import {BoolEdit} from "../edit/BoolEdit";
import {SelectEdit} from "../edit/SelectEdit";
import {NumberEdit} from "../edit/NumberEdit";
import {DateEdit} from "../edit/DateEdit";
import {TemporalExtentsEdit} from "../edit/TemporalExtentsEdit";
import {ExtentEdit} from "../edit/ExtentEdit";

const LABEL_COL_WIDTH = 3;
const VALUE_COL_WIDTH = 12 - LABEL_COL_WIDTH;

const labelCol = (text: string) => <Col sm={LABEL_COL_WIDTH} className="detail-label">{text}</Col>;
const valueCol = (val: any, help?: string, cssClass = "detail-value") => {
  return <Col sm={VALUE_COL_WIDTH} className={cssClass}>
    <div className="detailViewValue">
        {val}
        {help ? <HelpBlock>{help}</HelpBlock> : null}
      </div>
  </Col>;
}


const DETAIL_VIEW_MESSAGES = defineMessages({
  createdBy: {
    id: "studio.detail-view.common-metadata.created-by",
    defaultMessage: "Created By",
  },
  createdTime: {
    id: "studio.detail-view.common-metadata.created-date",
    defaultMessage: "Date Created",
  },
});

export enum Formats {
  TEXT,
  TEXT_EDIT,
  DOUBLE_EDIT,
  LONG_EDIT,
  BOOL_EDIT,
  BOUNDS,
  DATETIME,
  FILESIZE,
  DATETIME_EDIT,
  TEXT_AREA,
  CHECKBOX,
  SELECT,
  TEMPORAL_EXTENTS_EDIT,
  EXTENT_EDIT
}

export interface FieldInfo {
  key: any;
  name: any;
  value: any;
  help?: string;
  format?: Formats;
  options?: string[];
  onChange?: (changedItem: any) => any;
  deleteButton?: JSX.Element;
  labelForOption?: (label: any) => any;
  helpForOption?: (help: any) => any;
}

interface DetailViewProps {
  fields: FieldInfo[];
  editExtentButton?: () => void;
}

export const humanReadableFileSize = (fileSizeInBytes: number) => {
  let i = -1;
  const byteUnits = [" kB", " MB", " GB", " TB", "PB", "EB", "ZB", "YB"];
  do {
    fileSizeInBytes = fileSizeInBytes / 1024;
    i++;
  } while (fileSizeInBytes > 1024);

  return Math.max(fileSizeInBytes, 0.1).toFixed(1) + byteUnits[i];
};

export class DetailView extends React.Component<DetailViewProps, {}> {

  createRowUI = ({
                   key,
                   name,
                   value,
                   help,
                   format = Formats.TEXT,
                   options,
                   helpForOption,
                   labelForOption,
                   onChange,
                   deleteButton
                 }: FieldInfo) => {

    switch (format) {
    case Formats.TEXT_EDIT:
      return (
          <div className="form-group detail-row" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "detailViewValue" : "detailViewValue-noDelete"}>
                <TextEdit value={value} onChange={onChange}/>
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help, "detail-row-textedit")}
          </div>
      );
    case Formats.LONG_EDIT:
      return (
          <div className="form-group detail-row" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "detailViewValue" : "detailViewValue-noDelete"}>
                <NumberEdit isLong={true} value={value} onChange={onChange}/>
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help, "detail-row-textedit")}
          </div>
      );
    case Formats.DOUBLE_EDIT:
      return (
          <div className="form-group detail-row" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "detailViewValue" : "detailViewValue-noDelete"}>
                <NumberEdit isDouble={true} value={value} onChange={onChange}/>
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help, "detail-row-textedit")}
          </div>
      );
    case Formats.BOOL_EDIT:
      return (
          <div className="form-group detail-row" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "detailViewValue" : "detailViewValue-noDelete"}>
                <BoolEdit value={value} onChange={onChange}/>
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help, "detail-row-booledit")}
          </div>
      );
    case Formats.FILESIZE: {
      const formattedSize = value ? `${humanReadableFileSize(value)}` : "-";
      return (
          <div className="form-group detail-row" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "detailViewValue" : "detailViewValue-noDelete"}>
                {formattedSize}
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help)}
          </div>
      );
    }
    case Formats.DATETIME: {
      const formattedDateVal = value ? `${DateUtil.formatDateTime(value)}` : "-";
      return (
          <div className="form-group detail-row" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "detailViewValue" : "detailViewValue-noDelete"}>
                {formattedDateVal}
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help)}
          </div>
      );
    }
    case Formats.DATETIME_EDIT: {
      return (
          <div className="form-group detail-row" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "detailViewValue" : "detailViewValue-noDelete"}>
                <DateEdit value={value} onChange={onChange}/>
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help, "detail-row-textedit")}
          </div>
      );
    }
    case Formats.BOUNDS: {
      return (
          <div key={key}>
            <div className="form-group detail-row">
              {labelCol(name)}
              {valueCol(`${value.x}, ${value.y} (x, y)`, help)}
            </div>
            <div className="form-group detail-row">
              {labelCol("")}
              {valueCol(`${value.width}, ${value.height} (width, height)`, helpForOption?.(value) ?? help)}
            </div>
            {deleteButton}
          </div>
      );
    }
    case Formats.TEXT_AREA: {
      return (
          <div className="form-group detail-row detail-value-text-area" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "detailViewValue" : "detailViewValue-noDelete"}>
                <FormControl componentClass="textarea" className="detail-text-area" disabled value={value}/>
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help)}
          </div>
      );
    }
    case Formats.SELECT: {
      return (
          <div className="form-group detail-row" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "detailViewValue" : "detailViewValue-noDelete"}>
                <SelectEdit value={value}
                            options={options.map((option) => {
                              return {key: option, value: option, label: labelForOption?.(option) ?? option};
                            })}
                            onChange={onChange}/>
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help)}
          </div>
      );
    }
    case Formats.TEMPORAL_EXTENTS_EDIT: {
      return (
          <div className="form-group detail-row" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "detailViewValue" : "detailViewValue-noDelete"}>
                <TemporalExtentsEdit value={value} onChange={onChange}/>
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help, "detail-row-textedit")}
          </div>
      );
    }
    case Formats.EXTENT_EDIT: {
      return (
          <div className="form-group detail-row" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "extentsValue detailViewValue" : "extentsValue detailViewValue-noDelete"}>
                <ExtentEdit value={value} onChange={onChange} editExtentButton={this.props.editExtentButton}/>
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help, "detail-row-textedit")}
          </div>
      );
    }
    case Formats.CHECKBOX: {
      return (
          <div className="form-group detail-row" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "detailViewValue" : "detailViewValue-noDelete"}>
                <Checkbox checked={value === true}
                          onChange={(event) => onChange((event.target as HTMLInputElement).checked)}/>
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help)}
          </div>
      )
    }
    default: {
      return (
          <div className="form-group detail-row" key={key}>
            {labelCol(name)}
            {valueCol(<div className={"detailViewValueCol"}>
              <div className={deleteButton ? "detailViewValue" : "detailViewValue-noDelete"}>
                {value ?? "-"}
              </div>
              {deleteButton}
            </div>, helpForOption?.(value) ?? help)}
          </div>
      );
    }
    }
  }

  render() {
    return (
        <div className="detail-view">
          {this.props.fields.map((field) => this.createRowUI(field))}
        </div>
    );
  }
}

interface DetailViewRowProps {
  name: string;
}

export class DetailViewRow extends React.Component
                                       <DetailViewRowProps, {}> {
  render() {
    const {name} = this.props;
    return (
        <div className="form-group" key={name}>
          {labelCol(name)}
          <Col sm={VALUE_COL_WIDTH}>{this.props.children}</Col>
        </div>
    );
  }
}

interface CommonMetadata {
  createdBy?: { username: string };
  creationTime?: string;
}

export const createCommonMetadataFields = (object: CommonMetadata, intl: InjectedIntl): FieldInfo[] => {
  const createdBy = object.createdBy ? object.createdBy.username : "Unknown";
  const creationTime = object.creationTime ? object.creationTime : "Unknown";
  return [
    {
      key: "Created By",
      name: intl.formatMessage(DETAIL_VIEW_MESSAGES.createdBy),
      value: createdBy,
    },
    {
      key: "Date Created",
      name: intl.formatMessage(DETAIL_VIEW_MESSAGES.createdTime),
      value: creationTime,
      format: Formats.DATETIME,
    },
  ];
};